import * as yup from 'yup';

import {FORMAT, KIND} from './consts';
import getUserCountryDialCode from './phoneCountryCodes';

// [ZOOM-707251] Increase characters limit to 1000
export const MAX_BOOKER_TEXT_INPUT = 1000;

/**
 * @typedef CustomField
 * @property {string} name
 * @property {boolean} enabled
 * @property {boolean} required
 * @property {FORMAT} format
 * @property {Number} position
 * @property {Array<string>} answerChoices
 * @property {boolean} includeOther
 */

/**
 * function to build dynamic schema based on customFields
 * @param {?Array<CustomField>} customFields server appt.customFields
 * @return {Object} object mapping field idx keys to yup schema according to field format
 */
export const createCustomSchema = (customFields) => {
  if (!customFields) {
    return {};
  };
  const yupSchema = {};
  customFields.forEach((customField, i) => {
    let yupFieldSchema;
    if (!customField.enabled) {
      return;
    }
    if (customField.format === FORMAT.STRING) {
      yupFieldSchema = yup.string()
        .max(MAX_BOOKER_TEXT_INPUT, `Can not be longer than ${MAX_BOOKER_TEXT_INPUT} characters`);
    }
    if (customField.format === FORMAT.TEXT) {
      yupFieldSchema = yup.string()
        .max(MAX_BOOKER_TEXT_INPUT, `Can not be longer than ${MAX_BOOKER_TEXT_INPUT} characters`);
    }
    if (customField.format === FORMAT.SELECT) {
      yupFieldSchema = yup.string();
    }
    if (customField.format === FORMAT.CHOICES_ONE) {
      yupFieldSchema = yup.string();
    }
    if (customField.format === FORMAT.CHOICES_MANY) {
      yupFieldSchema = yup.array();
    }
    if (customField.format === FORMAT.PHONE_NUMBER) {
      yupFieldSchema = yup.object({
        countryCode: yup.string()
          .test(
            'country code', 'Invalid country code', function(val, context) {
              if (context.parent.phoneNumber !== '') {
                // When phone number is not empty, this field must be populated
                return !!val.match(/^\+[0-9]{1,5}$/);
              }
              return !!val.match(/^\+[0-9]{1,5}$/) || val === '';
            }
          ),
        phoneNumber: yup.string()
          .test('valid or empty', 'Invalid phone number', function(val, context) {
            // Phone Number Input
            return !!val.match(/^[0-9]{5,20}$/) || val === '';
          }),
      });
    }
    if (customField.required && customField.format === FORMAT.CHOICES_MANY) {
      yupFieldSchema = yupFieldSchema
        .of(
          yup.object({
            checked: yup.boolean(),
            value: yup.string(),
          })
        )
        .test({
          name: 'has-one-true',
          message: 'This is a required field',
          test: (val) => val.some((v) => v.checked && v.value),
        })
        .required();
    } else if (customField.required && customField.format === FORMAT.PHONE_NUMBER) {
      yupFieldSchema = yup.object({
        countryCode: yup.string()
          .required('This is a required field')
          .matches(/^\+[0-9-]{1,5}$/, 'Invalid country code'),
        phoneNumber: yup.string()
          .required('This is a required field')
          .matches(/^[0-9]{5,20}$/, 'Invalid phone number'),
      });
    } else if (customField.required) {
      yupFieldSchema = yupFieldSchema.required('This is a required field');
    }
    yupSchema[`field${i}`] = yupFieldSchema;
  });
  return yupSchema;
};

/**
 * getCustomValues
 * @param {Array<CustomField>} apptCustomFields Server appt.customFields
 * @param {?Array} existingCustomValues any existing answers that booker previously gave
 * @return {Object}
 */
export const getCustomFieldDefaultValues = (apptCustomFields = [], existingCustomValues = []) => {
  const customFieldDefaults = {};
  if (!existingCustomValues || existingCustomValues.length === 0) {
    apptCustomFields.forEach((customField, i) => {
      if (!customField.enabled) {
        return null;
      };
      const fieldNum = `field${i}`;
      if (customField.format === FORMAT.CHOICES_MANY) {
        const values = customField.answerChoices.map((ac) => {
          return {
            value: ac,
            checked: false,
          };
        });
        if (customField.includeOther) {
          values.push({value: '', checked: false});
        }
        customFieldDefaults[fieldNum] = values;
      } else if (customField.format === FORMAT.PHONE_NUMBER) {
        customFieldDefaults[fieldNum] = {
          countryCode: getUserCountryDialCode() || '',
          phoneNumber: '',
        };
      } else if (customField.format === FORMAT.CHOICES_ONE) {
        customFieldDefaults[fieldNum] = '';
      } else if (customField.format === FORMAT.STRING) {
        customFieldDefaults[fieldNum] = '';
      } else if (customField.format === FORMAT.TEXT) {
        customFieldDefaults[fieldNum] = '';
      } else if (customField.format === FORMAT.SELECT) {
        if (customField.required) {
          customFieldDefaults[fieldNum] = customField.answerChoices[0];
        } else {
          customFieldDefaults[fieldNum] = '';
        }
      }
    });
  } else {
    apptCustomFields.forEach((customField, i) => {
      if (!customField.enabled) {
        return null;
      };
      const fieldNum = `field${i}`;
      const customValue = existingCustomValues?.find((cv) => cv.position === customField.position);
      const hasVal = !!customValue;
      const firstVal = customValue?.values[0];
      if (customField.format === FORMAT.CHOICES_MANY) {
        if (hasVal) {
          const arrWithBool = [];
          customField.answerChoices.forEach((ac) => {
            if (customValue.values.includes(ac)) {
              arrWithBool.push({
                value: ac,
                checked: true,
              });
            } else {
              arrWithBool.push({
                value: ac,
                checked: false,
              });
            }
          });
          if (customField.includeOther) {
            const otherVal = customValue.values?.find((val) => !customField.answerChoices.includes(val));
            arrWithBool.push({
              value: otherVal || '',
              checked: !!otherVal,
            });
          }
          customFieldDefaults[fieldNum] = arrWithBool;
        } else {
          const arrWithBool = [];
          customField.answerChoices.forEach((ac) => {
            arrWithBool.push({
              value: ac,
              checked: false,
            });
          });
          if (customField.includeOther) {
            arrWithBool.push({
              value: '',
              checked: false,
            });
          }
          customFieldDefaults[fieldNum] = arrWithBool;
        }
      } else if (customField.format === FORMAT.PHONE_NUMBER) {
        customFieldDefaults[fieldNum] = {
          countryCode: getUserCountryDialCode(),
          phoneNumber: '',
        };
        if (hasVal) {
          // if (firstVal?.indexOf('+') > -1) {
          //   firstVal = firstVal.slice(1);
          // }
          const arr = firstVal.split(' ');
          if (arr.length === 2) {
            customFieldDefaults[fieldNum] = {
              countryCode: arr[0],
              phoneNumber: arr[1],
            };
          }
        }
      } else if (customField.format === FORMAT.CHOICES_ONE) {
        if (hasVal) {
          customFieldDefaults[fieldNum] = firstVal;
        } else {
          customFieldDefaults[fieldNum] = '';
        }
      } else if (customField.format === FORMAT.STRING) {
        if (hasVal) {
          customFieldDefaults[fieldNum] = firstVal;
        } else {
          customFieldDefaults[fieldNum] = '';
        }
      } else if (customField.format === FORMAT.TEXT) {
        if (hasVal) {
          customFieldDefaults[fieldNum] = firstVal;
        } else {
          customFieldDefaults[fieldNum] = '';
        }
      } else if (customField.format === FORMAT.SELECT) {
        if (hasVal) {
          customFieldDefaults[fieldNum] = firstVal;
        } else {
          customFieldDefaults[fieldNum] = '';
        }
      }
    });
  }
  return customFieldDefaults;
};

/**
 * Convert custom values from the form state to server payload accepted format
 * @param {Object} formValues
 * @param {RoutingQuestion[]} fields routing form questions
 * @return {Array}
 */
export const formatCustomValuesRoutingResponses = (formValues, fields) => {
  const customArr = [];
  const bookerCustom = formValues?.customFields || [];

  // {
  //   "questionsAndAnswers": [
  //      {
  //        "answers": [
  //          "hiking",
  //          "cooking"
  //        ],
  //        "id": "question-id",
  //        "question": "Hobbit"
  //      }
  //    ]
  //  }
  fields.forEach((customField, i) => {
    if (customField?.kind === KIND.SELECT) {
      const multipleChoice = (bookerCustom[`field${i}`] ?? [])
        .filter((item) => item.checked)
        .map((item) => item?.value);
      if (multipleChoice.length === 0) {
        return;
      }
      customArr.push({
        answers: multipleChoice,
        id: customField.id,
        question: customField.label,
      });
    } else if (customField?.kind === KIND.PHONE) {
      const {countryCode, phoneNumber} = bookerCustom[`field${i}`];
      if (phoneNumber === '') {
        return;
      }

      const code = countryCode?.split('-')[0];
      const addPlus = code !== '' && !code.includes('+');
      const normalizedCountryCode = addPlus ? '+' + code : code;
      const phoneNum = [normalizedCountryCode, phoneNumber].join(' ');

      customArr.push({
        answers: [phoneNum],
        id: customField.id,
        question: customField.label,
      });
    } else {
      if (bookerCustom[`field${i}`] === '' || bookerCustom[`field${i}`] === undefined) {
        return;
      }
      customArr.push({
        answers: [bookerCustom[`field${i}`]],
        id: customField.id,
        question: customField.label,
      });
    }
  });
  return customArr;
};


/**
 * Convert custom values from the form state to server payload accepted format
 * @param {Object} formValues
 * @param {Array<CustomField>} apptFields server appt.customFields
 * @return {Array}
 */
export const formatCustomValues = (formValues, apptFields = []) => {
  const customArr = [];
  const bookerCustom = formValues?.customFields || [];

  apptFields.forEach((customField, i) => {
    if (!customField.enabled) {
      return null;
    }
    if (customField?.format === FORMAT.CHOICES_MANY) {
      console.log(bookerCustom[`field${i}`]);
      const selectedChoices = (bookerCustom[`field${i}`] ?? [])
        .filter((item) => item.checked && item.value)
        .map((item) => item.value);
      if (selectedChoices.length === 0) {
        return;
      }
      customArr.push({
        name: customField.name,
        position: customField.position,
        values: selectedChoices,
        format: customField.format,
        required: customField.required,
        includeOther: customField.includeOther,
        id: customField.id,
      });
    } else if (customField?.format === FORMAT.PHONE_NUMBER) {
      const {countryCode, phoneNumber} = bookerCustom[`field${i}`];
      if (phoneNumber === '') {
        return;
      }

      const code = countryCode?.split('-')[0];
      const addPlus = code !== '' && !code.includes('+');
      const normalizedCountryCode = addPlus ? '+' + code : code;
      const phoneNum = [normalizedCountryCode, phoneNumber].join(' ');

      customArr.push({
        name: customField.name,
        position: customField.position,
        values: [phoneNum],
        format: customField.format,
        required: customField.required,
        includeOther: customField.includeOther,
        id: customField.id,
      });
    } else {
      if (bookerCustom[`field${i}`] === '' || bookerCustom[`field${i}`] === undefined) {
        return;
      }
      customArr.push({
        name: customField.name,
        position: customField.position,
        format: customField.format,
        required: customField.required,
        values: [bookerCustom[`field${i}`]],
        includeOther: customField.includeOther,
        id: customField.id,
      });
    }
  });
  return customArr;
};
